import React, { Fragment, useMemo, useContext } from "react";
import PropTypes from "prop-types";
import BWModels from "@benjamin-west-rpm2/benjamin-west-models";

import FormGrid from "../../../../../forms/FormGrid";
import ActionButtons from "../../../../../forms/ActionButtons";
import { ProjectContext } from "../../../../../hooks/useIsProjectClosed";
import FormikForm from "../../../../../forms/FormikForm";
import propTypes from "../../../../../../constants/propTypes";

import { inputs } from "../../../../../../components/inputs/inputConfigs";
import { LabeledTextContext } from "../../../../../../withPORevision/withLabeledTextOption";
import { getEndOfDayInTimezone } from "../../../../../../utils/timezoneUtils";

const getFields = timezone => ({
  groups: [
    {
      items: [
        {
          input: { ...inputs.referenceNumber, required: true },
          grid: { xs: 8 },
        },
        {
          input: {
            ...inputs.amount,
            label: "Amount",
            required: true,
            inputProps: {
              maskProps: { allowNegative: true },
            },
          },
          grid: { xs: 4 },
        },
        {
          input: { ...inputs.paymentDate, required: true, timezone },
          grid: { xs: 4 },
        },
      ],
    },
  ],
});

const PaymentForm = ({
  onSubmit,
  fee,
  feePayment,
  toggleShowList,
  dataComponent,
  timezone,
}) => {
  const fields = useMemo(() => getFields(timezone), [timezone]);

  const normalizedPaymentDate = timezone
    ? getEndOfDayInTimezone(new Date(), timezone)
    : new Date();

  const initialValues = useMemo(() => {
    return (
      feePayment || {
        feeId: fee.id,
        amount: fee.amount - fee.amountPaid,
        paymentDate: normalizedPaymentDate,
      }
    );
  }, [fee.amount, fee.amountPaid, fee.id, feePayment, normalizedPaymentDate]);

  const validationAmount = Number(
    Number(fee.amount - fee.amountPaid + (feePayment?.amount || 0)).toFixed(2)
  );
  const validationSchema = useMemo(
    () =>
      BWModels.loadSchema("FeePayment").append({
        amount:
          validationAmount < 0
            ? BWModels.Joi.number()
                .min(validationAmount)
                .required()
            : BWModels.Joi.number()
                .max(validationAmount)
                .required(),
      }),
    [validationAmount]
  );
  const projectContext = useContext(ProjectContext);
  const isLabeled =
    fee.revenueSharing ||
    !["Billed", "Partially Paid", "Paid"].includes(fee.status);
  return (
    <ProjectContext.Provider
      value={{
        ...projectContext,
        ignoreProjectClosed: true,
      }}
    >
      <LabeledTextContext.Provider value={isLabeled}>
        <FormikForm
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {({ handleSubmit, values, errors, ...formikProps }) => (
            <Fragment>
              <FormGrid
                fields={fields}
                values={values}
                errors={errors}
                {...formikProps}
              />
              <ActionButtons
                isModal
                onSend={handleSubmit}
                onCancel={toggleShowList}
                sendButtonText="Save Payment"
                listeners={[dataComponent.dataComponentId]}
                disabled={isLabeled}
              />
            </Fragment>
          )}
        </FormikForm>
      </LabeledTextContext.Provider>
    </ProjectContext.Provider>
  );
};

PaymentForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  fee: propTypes.fee,
  feePayment: propTypes.feePayment,
  toggleShowList: PropTypes.func,
  dataComponent: propTypes.dataComponent,
  timezone: PropTypes.string,
};

export default PaymentForm;
